// ═══════════════════════════════════════════════════════════════════════════
// QUELLE DATE CHOISIT LE BARÈME — un seul endroit, pour toute l'application.
// ───────────────────────────────────────────────────────────────────────────
// POURQUOI CE FICHIER EXISTE (11/08/2026, audit « le bonus est-il branché à 360° »).
//
// Le bonus à l'emploi n'est pas un barème unique : c'est une SUITE de paliers
// datés (`LB.pp.werkbonusSocialParPalier` — 01/04/2026, puis +2 % au 01/07/2026).
// `calcPayroll` choisit le bon palier à partir de `opts.date`. SANS date, il
// retombe volontairement sur le palier BASELINE — le PLUS ANCIEN — pour que les
// 29 fiches Liantis certifiées et les fixtures de parité ne dérivent jamais.
//
// 🛑 CE DÉFAUT EST JUSTE POUR UNE FIXTURE, FAUX POUR UN ÉCRAN. Mesuré ce jour :
// un employé à 2 300 € voyait 281,65 € de bonus au lieu de 299,53 €, et 2 045,96 €
// de net au lieu de 2 056,18 € — soit 17,88 € de bonus et 10,22 € de net en
// moins, chaque mois, sur TOUT écran qui n'avait pas passé de date. La fiche de
// paie, elle, était juste : c'est un des rares chemins qui passait la période.
// L'écart ne se voit pas à l'œil (les deux montants sont plausibles) et ne casse
// rien — exactement la signature des deux défauts corrigés la veille.
//
// ⚠️ CE FICHIER NE CONTIENT AUCUNE VALEUR LÉGALE (R1). Il fabrique une DATE, et
// il LIT la table datée de `lois-belges.js` pour dire quel palier est en vigueur —
// il n'en recopie aucun montant. Changer un barème ou le calendrier des
// indexations se fait dans `lois-belges.js`, jamais ici.
//
// RÈGLE D'EMPLOI, en deux lignes :
//   · un écran, une API, un export, un document → `optsPalier(periode)` ;
//   · une fixture certifiée, une baseline de parité → RIEN, on garde le défaut.
// Le garde-fou `__tests__/bonus-palier-courant.test.js` fige cette frontière.
// ═══════════════════════════════════════════════════════════════════════════

import { LB } from './lois-belges';

/** Deux chiffres, toujours (1 → '01'). */
const dd = (n) => String(n).padStart(2, '0');

/**
 * Premier jour du mois courant, en 'YYYY-MM-01'.
 * Le JOUR n'a aucune importance pour le choix du palier (les paliers commencent
 * tous un 1er) ; le mois, lui, est ce qui compte.
 */
export function moisCourantISO() {
  const d = new Date();
  return `${d.getFullYear()}-${dd(d.getMonth() + 1)}-01`;
}

/**
 * Normalise en 'YYYY-MM-01' la façon dont chaque appelant exprime sa période.
 * Accepte : { year, month } · { annee, mois } · 'YYYY-MM' · 'YYYY-MM-JJ' · Date.
 * Rien d'exploitable → mois courant (c'est ce qu'un utilisateur devant un
 * simulateur attend : le barème d'aujourd'hui, pas celui d'un palier passé).
 */
export function datePalier(periode) {
  if (!periode) return moisCourantISO();
  if (periode instanceof Date && !isNaN(periode)) {
    return `${periode.getFullYear()}-${dd(periode.getMonth() + 1)}-01`;
  }
  if (typeof periode === 'string') {
    const m = periode.match(/^(\d{4})-(\d{2})/);
    return m ? `${m[1]}-${m[2]}-01` : moisCourantISO();
  }
  if (typeof periode === 'object') {
    const y = periode.year ?? periode.annee;
    const mo = periode.month ?? periode.mois;
    if (y && mo) return `${y}-${dd(mo)}-01`;
  }
  return moisCourantISO();
}

/**
 * Prêt à passer au moteur : `calcPayroll(brut, …, regime, optsPalier(periode))`.
 * @param {*} periode  cf. datePalier ; omis → mois courant
 * @param {object} extra  autres options du moteur à conserver (ATN, CSSS réel…)
 */
export function optsPalier(periode, extra) {
  return { ...(extra || {}), date: datePalier(periode) };
}

/**
 * Le palier de barème du bonus à l'emploi EN VIGUEUR à cette période, tel quel
 * (l'objet de `LB.pp.werkbonusSocialParPalier`, jamais une copie de ses montants).
 *
 * À quoi ça sert : un écran qui AFFICHE les plafonds de référence doit montrer
 * ceux du mois courant. L'alias `LB.pp.werkbonusSocial` est, lui, figé sur la
 * BASELINE 01/04/2026 — il est marqué `_deprecated` justement pour ça, et
 * l'écran werkbonus l'affichait quand même.
 *
 * ⚠️ La règle de sélection est la même que celle du moteur (`werkbonusPalier`,
 * privé) : le dernier palier dont `from` est ≤ à la date. Le garde-fou
 * `__tests__/bonus-palier-courant.test.js` compare les deux sur chaque date de
 * bascule — si elles divergent un jour, il tombe.
 */
export function palierWerkbonus(periode) {
  const paliers = LB?.pp?.werkbonusSocialParPalier;
  if (!Array.isArray(paliers) || !paliers.length) return null;
  const d = datePalier(periode);
  let sel = paliers[0];
  for (const p of paliers) { if (p.from <= d) sel = p; else break; }
  return sel;
}

export default optsPalier;
